/**
 * Error handlers to improve application stability
 */

/**
 * Suppresses non-critical ResizeObserver errors
 * This uses several approaches to handle different error reporting mechanisms
 */
export const suppressResizeObserverErrors = (): void => {
  // Method 1: Override the ResizeObserver constructor
  if (typeof window !== 'undefined' && window.ResizeObserver) {
    const originalResizeObserver = window.ResizeObserver;
    
    window.ResizeObserver = class ExtendedResizeObserver extends originalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
          // Wrap the callback in a try-catch to prevent uncaught exceptions
          try {
            callback(entries, observer);
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('ResizeObserver callback error suppressed:', e);
            }
          }
        };
        super(wrappedCallback);
      }
    };
  }
  
  // Method 2: Event listener for error events
  const errorHandler = (event: ErrorEvent) => {
    if (event.message && (event.message.includes('ResizeObserver loop') || 
                          event.message.includes('ResizeObserver was not able'))) {
      // Prevent the error from appearing in the console
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };
  
  window.addEventListener('error', errorHandler, true);
  
  // Method 3: Override console.error
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    if (args.length > 0 && 
        typeof args[0] === 'string' && 
        args[0].includes('ResizeObserver loop')) {
      return; // Suppress this specific error
    }
    originalConsoleError.apply(console, args);
  };
  
  if (process.env.NODE_ENV === 'development') {
    console.info('Enhanced ResizeObserver error suppression is active');
  }
};
